<template>
  <v-container>
    <v-row class="mb-4">
      <v-col cols="12">
        <h1>Payroll</h1>
        <p class="text-body1 text-medium-emphasis">Saved payroll reports across all gigs</p>
      </v-col>
    </v-row>

    <v-alert v-if="!canView" type="info" variant="tonal">Payroll is visible to managers, admins and owners.</v-alert>

    <template v-else>
      <v-alert v-if="store.error" type="error" closable class="mb-4" @click:close="store.error = null">{{ store.error }}</v-alert>

      <v-card class="mb-4" variant="tonal">
        <v-card-text class="d-flex flex-wrap align-center" style="gap: 24px">
          <div>
            <div class="text-caption text-medium-emphasis">Awaiting payment</div>
            <div class="text-h6" data-testid="unpaid-total">${{ money(unpaidTotal) }}</div>
          </div>
          <div>
            <div class="text-caption text-medium-emphasis">Reports</div>
            <div class="text-h6">{{ unpaidCount }} finalized · {{ draftCount }} draft</div>
          </div>
          <div v-if="owed.draftTotal > 0">
            <div class="text-caption text-medium-emphasis">Not yet finalized</div>
            <div class="text-h6 text-warning" data-testid="draft-total">${{ money(owed.draftTotal) }}</div>
          </div>
          <v-spacer />
          <v-btn-toggle v-model="mode" mandatory density="compact" variant="outlined" divided data-testid="view-toggle">
            <v-btn value="gig">By gig</v-btn>
            <v-btn value="person" data-testid="by-person">By person</v-btn>
          </v-btn-toggle>
          <v-btn-toggle v-if="mode === 'gig'" v-model="filter" mandatory density="compact" variant="outlined" divided>
            <v-btn value="unpaid">Unpaid</v-btn>
            <v-btn value="paid">Paid</v-btn>
            <v-btn value="all">All</v-btn>
          </v-btn-toggle>
        </v-card-text>
      </v-card>

      <v-card v-if="mode === 'gig'">
        <v-card-text v-if="shown.length === 0" class="text-center py-12 text-medium-emphasis">No payroll reports to show</v-card-text>
        <v-table v-else>
          <thead>
            <tr>
              <th>
                <v-checkbox-btn :model-value="allSelected" :indeterminate="selected.length > 0 && !allSelected" @update:model-value="toggleAll" />
              </th>
              <th>Gig</th>
              <th>Status</th>
              <th class="text-right">People</th>
              <th class="text-right">Total</th>
              <th>Updated</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="r in shown" :key="r.gigId">
              <tr>
                <td>
                  <v-checkbox-btn v-if="r.state === 'PROCESSED'" v-model="selected" :value="r.gigId" />
                </td>
                <td>
                  <router-link :to="{ name: 'GigDetail', params: { gigId: r.gigId }, query: { org: r.orgId } }">{{ r.gigName }}</router-link>
                  <div v-if="r.warnings.length" class="text-caption text-warning">{{ r.warnings.length }} note{{ r.warnings.length === 1 ? '' : 's' }}</div>
                </td>
                <td>
                  <v-chip size="small" :color="stateColor(r.state)">{{ stateLabel(r.state) }}</v-chip>
                  <div v-if="r.state === 'PAID' && r.paymentReference" class="text-caption text-medium-emphasis">{{ r.paymentReference }}</div>
                  <div v-if="r.state === 'PROCESSED' && hasAnyPaidLine(r)" class="text-caption text-warning">
                    Partly paid — ${{ money(reportPaidTotals(r).paid) }} of ${{ money(r.totalCost) }}
                  </div>
                </td>
                <td class="text-right">{{ r.entries.length + r.fees.length }}</td>
                <td class="text-right">${{ money(r.totalCost) }}</td>
                <td>{{ formatDate(r.paidAt ?? r.processedAt ?? r.generatedAt) }}</td>
                <td class="text-right text-no-wrap">
                  <v-btn size="x-small" variant="text" :data-testid="'expand-' + r.gigId" @click="toggle(r.gigId)">
                    {{ expanded.has(r.gigId) ? 'Hide' : 'Summary' }}
                  </v-btn>
                  <v-btn size="x-small" variant="text" @click="store.downloadCsv(r, r.gigName)">CSV</v-btn>
                  <v-btn
                    v-if="r.state !== 'DRAFT'"
                    size="x-small"
                    variant="text"
                    :loading="store.busy && pdfFor === r.gigId"
                    :data-testid="'pdf-' + r.gigId"
                    @click="downloadPdf(r)"
                  >
                    PDF
                  </v-btn>
                </td>
              </tr>
              <!-- Summary: who is owed what on this gig, without opening it. -->
              <tr v-if="expanded.has(r.gigId)" :data-testid="'summary-' + r.gigId">
                <td></td>
                <td colspan="6" class="py-2">
                  <v-table density="compact" class="bg-transparent">
                    <thead>
                      <tr>
                        <th>Person</th>
                        <th>Rate</th>
                        <th class="text-right">Worked h</th>
                        <th class="text-right">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="e in r.entries" :key="e.bookingId">
                        <td>{{ e.name }}<span class="text-caption text-medium-emphasis"> — {{ e.role }}<template v-if="e.label"> ({{ e.label }})</template><template v-if="e.sharedWith?.length"> · shared day with {{ e.sharedWith.join(', ') }}</template></span></td>
                        <td>${{ e.rate.toLocaleString() }}/{{ e.rateType === 'hourly' ? 'hr' : 'day' }}</td>
                        <td class="text-right">{{ e.workedHours.toFixed(2) }}</td>
                        <td class="text-right">${{ money(e.grossPay) }}</td>
                      </tr>
                      <tr v-for="f in r.fees" :key="f.bookingId + 'fee'">
                        <td>{{ f.name }}<span class="text-caption text-medium-emphasis"> — cancellation fee</span></td>
                        <td colspan="2" class="text-caption text-medium-emphasis">{{ f.reason }}</td>
                        <td class="text-right">${{ money(f.amount) }}</td>
                      </tr>
                    </tbody>
                  </v-table>
                  <div v-for="w in r.warnings" :key="w" class="text-caption text-warning mt-1">{{ w }}</div>
                </td>
              </tr>
            </template>
          </tbody>
        </v-table>
        <v-card-actions v-if="selected.length">
          <v-spacer />
          <v-btn color="primary" variant="flat" data-testid="batch-paid" @click="openBatch">Mark {{ selected.length }} paid…</v-btn>
        </v-card-actions>
      </v-card>

      <!-- By person: what each person is still owed across all finalized gigs. -->
      <v-card v-else data-testid="person-view">
        <v-card-text v-if="peopleRows.length === 0" class="text-center py-12 text-medium-emphasis">
          Nobody is owed anything right now
        </v-card-text>
        <v-table v-else>
          <thead>
            <tr>
              <th>Person</th>
              <th class="text-right">Gigs</th>
              <th class="text-right">Hours</th>
              <th class="text-right">Owed</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <template v-for="p in peopleRows" :key="p.personId">
              <tr :data-testid="'person-' + p.personId">
                <td>{{ p.name }}</td>
                <td class="text-right">{{ p.gigs.length }}</td>
                <td class="text-right">{{ p.workedHours.toFixed(2) }}</td>
                <td class="text-right" :class="p.total > 0 ? 'font-weight-medium' : 'text-medium-emphasis'">${{ money(p.total) }}</td>
                <td class="text-right text-no-wrap">
                  <v-btn size="x-small" variant="text" :data-testid="'person-expand-' + p.personId" @click="togglePerson(p.personId)">
                    {{ expandedPeople.has(p.personId) ? 'Hide' : 'Details' }}
                  </v-btn>
                  <v-btn v-if="p.total > 0" size="x-small" color="primary" variant="flat" :data-testid="'pay-' + p.personId" @click="openPay(p)">Pay…</v-btn>
                </td>
              </tr>
              <tr v-if="expandedPeople.has(p.personId)" :data-testid="'person-detail-' + p.personId">
                <td colspan="5" class="py-2">
                  <div v-if="p.gigs.length">
                    <div class="text-caption text-medium-emphasis mb-1">Unpaid</div>
                    <v-table density="compact" class="bg-transparent">
                      <tbody>
                        <tr v-for="g in p.gigs" :key="g.gigId">
                          <td>
                            <router-link :to="{ name: 'GigDetail', params: { gigId: g.gigId }, query: { org: g.orgId } }">{{ g.gigName }}</router-link>
                            <div v-for="l in g.lines" :key="l.bookingId + l.kind" class="text-caption text-medium-emphasis">
                              <template v-if="l.kind === 'work'">{{ l.role }}<template v-if="l.label"> ({{ l.label }})</template> — {{ (l.workedHours ?? 0).toFixed(2) }} h</template>
                              <template v-else>Cancellation fee<template v-if="l.reason"> — {{ l.reason }}</template></template>
                            </div>
                          </td>
                          <td class="text-right">${{ money(g.amount) }}</td>
                        </tr>
                      </tbody>
                    </v-table>
                  </div>
                  <div v-if="p.payments.length" :class="p.gigs.length ? 'mt-3' : ''">
                    <div class="text-caption text-medium-emphasis mb-1">Payment history</div>
                    <v-table density="compact" class="bg-transparent">
                      <tbody>
                        <tr v-for="pay in p.payments" :key="pay.id">
                          <td>{{ formatDate(pay.paidAt) }}<span v-if="pay.reference" class="text-caption text-medium-emphasis"> — {{ pay.reference }}</span></td>
                          <td class="text-caption text-medium-emphasis">{{ gigCount(pay) }} {{ gigCount(pay) === 1 ? 'gig' : 'gigs' }}</td>
                          <td class="text-right">${{ money(pay.total) }}</td>
                          <td class="text-right">
                            <v-btn size="x-small" variant="text" :data-testid="'statement-' + pay.id" @click="downloadStatement(pay)">Statement PDF</v-btn>
                          </td>
                        </tr>
                      </tbody>
                    </v-table>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </v-table>
      </v-card>
    </template>

    <v-dialog v-model="batchOpen" max-width="420">
      <v-card>
        <v-card-title>Mark {{ selected.length }} {{ selected.length === 1 ? 'report' : 'reports' }} paid</v-card-title>
        <v-card-text>
          <p class="text-body2 mb-3">Total ${{ money(selectedTotal) }}</p>
          <v-text-field v-model="paidDate" type="date" label="Date paid" />
          <v-text-field v-model="paidRef" label="Reference (optional)" hint="Applied to every selected report" persistent-hint maxlength="200" />
          <v-alert v-if="batchError" type="error" density="compact" class="mt-3">{{ batchError }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="batchOpen = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" :loading="batching" data-testid="confirm-batch" @click="markBatch">Mark paid</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Pay one person: choose which of their unpaid gigs this payment covers. -->
    <v-dialog v-model="payOpen" max-width="520">
      <v-card v-if="payFor">
        <v-card-title>Pay {{ payFor.name }}</v-card-title>
        <v-card-text>
          <div class="text-caption text-medium-emphasis mb-1">This payment covers</div>
          <div v-for="g in payFor.gigs" :key="g.gigId" class="d-flex align-center">
            <v-checkbox-btn v-model="payGigs" :value="g.gigId" :data-testid="'pay-gig-' + g.gigId" />
            <span class="flex-grow-1">{{ g.gigName }}</span>
            <span>${{ money(g.amount) }}</span>
          </div>
          <div class="d-flex justify-space-between font-weight-medium mt-2 mb-4">
            <span>Total</span>
            <span data-testid="pay-total">${{ money(payTotal) }}</span>
          </div>
          <v-text-field v-model="payDate" type="date" label="Date paid" />
          <v-text-field v-model="payRef" label="Reference (optional)" hint="Check number, ACH batch, etc." persistent-hint maxlength="200" data-testid="pay-reference" />
          <p class="text-caption text-medium-emphasis mt-3 mb-0">
            Recording this marks those gigs paid for {{ payFor.name }} and downloads a statement PDF to send with the payment.
          </p>
          <v-alert v-if="payError" type="error" density="compact" class="mt-3">{{ payError }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="payOpen = false">Cancel</v-btn>
          <v-btn color="primary" variant="flat" :disabled="payGigs.length === 0" :loading="paying" data-testid="confirm-pay" @click="submitPay">
            Record payment &amp; statement
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue';
import type { OwedGig, PayrollPayment, PayrollReport, PayrollState } from '@gigdriver/domain';
import { hasAnyPaidLine, owedByPerson, reportPaidTotals } from '@gigdriver/domain';
import { usePayrollStore } from '../stores/payroll';
import { useOrganizationStore } from '../stores/organization';

const store = usePayrollStore();
const orgStore = useOrganizationStore();

const canView = computed(() => ['owner', 'admin', 'manager'].includes(orgStore.myRole ?? ''));
// Coordinators/staff can't read payroll (rules), so don't even subscribe.
watch(
  () => [orgStore.orgId, canView.value] as const,
  ([id, ok]) => id && ok && store.watchOrg(id),
  { immediate: true },
);
onUnmounted(() => store.stopOrgWatch());

const mode = ref<'gig' | 'person'>('gig');
const filter = ref<'unpaid' | 'paid' | 'all'>('unpaid');

// ---- by person ----
const owed = computed(() => owedByPerson(store.reports));

interface PersonRow {
  personId: string;
  name: string;
  total: number;
  workedHours: number;
  gigs: OwedGig[];
  payments: PayrollPayment[];
}
// Everyone owed something, plus anyone paid before (so their statements stay
// reachable once they're settled up).
const peopleRows = computed<PersonRow[]>(() => {
  const rows = new Map<string, PersonRow>();
  for (const p of owed.value.people) rows.set(p.personId, { ...p, payments: [] });
  for (const pay of store.payments) {
    let row = rows.get(pay.personId);
    if (!row) {
      row = { personId: pay.personId, name: pay.name, total: 0, workedHours: 0, gigs: [], payments: [] };
      rows.set(pay.personId, row);
    }
    row.payments.push(pay);
  }
  return [...rows.values()].sort((a, b) => (b.total > 0 ? 1 : 0) - (a.total > 0 ? 1 : 0) || a.name.localeCompare(b.name));
});

const expandedPeople = ref(new Set<string>());
function togglePerson(id: string) {
  const next = new Set(expandedPeople.value);
  if (!next.delete(id)) next.add(id);
  expandedPeople.value = next;
}
const gigCount = (pay: PayrollPayment) => new Set(pay.lines.map(l => l.gigId)).size;
async function downloadStatement(pay: PayrollPayment) {
  try {
    await store.downloadStatement(pay.orgId, pay.id, pay.name);
  } catch {
    // surfaced via store.error
  }
}

const payOpen = ref(false);
const paying = ref(false);
const payError = ref<string | null>(null);
const payFor = ref<PersonRow | null>(null);
const payGigs = ref<string[]>([]);
const payDate = ref('');
const payRef = ref('');
const payTotal = computed(() => {
  const cents = (payFor.value?.gigs ?? []).filter(g => payGigs.value.includes(g.gigId)).reduce((t, g) => t + Math.round(g.amount * 100), 0);
  return cents / 100;
});
function openPay(p: PersonRow) {
  const d = new Date();
  payFor.value = p;
  payGigs.value = p.gigs.map(g => g.gigId);
  payDate.value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  payRef.value = '';
  payError.value = null;
  payOpen.value = true;
}
async function submitPay() {
  if (!payFor.value) return;
  paying.value = true;
  payError.value = null;
  try {
    // Local noon avoids a time-zone edge shifting the calendar day.
    const when = payDate.value ? new Date(`${payDate.value}T12:00:00`) : undefined;
    await store.payPerson(payFor.value.gigs[0].orgId, payFor.value.personId, payGigs.value, when, payRef.value.trim() || undefined);
    payOpen.value = false;
  } catch (err) {
    payError.value = err instanceof Error ? err.message : 'Failed to record payment';
    store.error = null;
  } finally {
    paying.value = false;
  }
}
const shown = computed(() =>
  store.reports.filter(r => (filter.value === 'all' ? true : filter.value === 'paid' ? r.state === 'PAID' : r.state !== 'PAID')),
);
const unpaid = computed(() => store.reports.filter(r => r.state === 'PROCESSED'));
// Per LINE now: a finalized report can be partly paid (person by person), so
// "awaiting payment" is the unpaid part of each, not its whole total.
const unpaidTotal = computed(() => unpaid.value.reduce((t, r) => t + reportPaidTotals(r).unpaid, 0));
const unpaidCount = computed(() => unpaid.value.length);
const draftCount = computed(() => store.reports.filter(r => r.state === 'DRAFT').length);

// Per-row summary expansion and PDF download, so a report can be reviewed or
// handed over without opening the gig.
const expanded = ref(new Set<string>());
function toggle(gigId: string) {
  const next = new Set(expanded.value);
  if (!next.delete(gigId)) next.add(gigId);
  expanded.value = next;
}
const pdfFor = ref<string | null>(null);
async function downloadPdf(r: PayrollReport) {
  pdfFor.value = r.gigId;
  try {
    await store.downloadPdf(r.orgId, r.gigId, r.gigName, r.version);
  } catch {
    // surfaced via store.error
  } finally {
    pdfFor.value = null;
  }
}

const selected = ref<string[]>([]);
const selectable = computed(() => shown.value.filter(r => r.state === 'PROCESSED').map(r => r.gigId));
const allSelected = computed(() => selectable.value.length > 0 && selectable.value.every(id => selected.value.includes(id)));
const selectedTotal = computed(() => store.reports.filter(r => selected.value.includes(r.gigId)).reduce((t, r) => t + r.totalCost, 0));
function toggleAll(on: boolean | null) {
  selected.value = on ? [...selectable.value] : [];
}

const batchOpen = ref(false);
const batching = ref(false);
const batchError = ref<string | null>(null);
const paidDate = ref('');
const paidRef = ref('');
function openBatch() {
  const d = new Date();
  paidDate.value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  paidRef.value = '';
  batchError.value = null;
  batchOpen.value = true;
}

// One callable per report; a failure doesn't stop the rest, and whatever
// failed stays selected so it can be retried.
async function markBatch() {
  batching.value = true;
  batchError.value = null;
  const failed: Array<{ gigId: string; name: string }> = [];
  const when = paidDate.value ? new Date(`${paidDate.value}T12:00:00`) : undefined;
  for (const gigId of selected.value) {
    const r = store.reports.find(x => x.gigId === gigId);
    if (!r) continue;
    try {
      await store.markPaid(r.orgId, gigId, when, paidRef.value.trim() || undefined);
    } catch {
      failed.push({ gigId, name: r.gigName });
    }
  }
  selected.value = failed.map(f => f.gigId);
  batching.value = false;
  if (failed.length) batchError.value = `Couldn't mark: ${failed.map(f => f.name).join(', ')}. ${store.error ?? ''}`.trim();
  else batchOpen.value = false;
}

const stateLabel = (s: PayrollState) => ({ DRAFT: 'Draft', PROCESSED: 'Finalized', PAID: 'Paid' })[s];
const stateColor = (s: PayrollState) => ({ DRAFT: 'warning', PROCESSED: 'primary', PAID: 'success' })[s];
const money = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
function formatDate(d?: Date): string {
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}
</script>
